import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { useStorage } from '../../hooks/useStorage';
import supabase from '../../lib/supabase';
import SafeIcon from '../../common/SafeIcon';
import ImageWithFallback from '../ImageWithFallback';
import * as FiIcons from 'react-icons/fi';

const {
  FiSave,
  FiUpload,
  FiLink,
  FiUser,
  FiTrash2,
  FiCheckCircle,
  FiAlertCircle,
  FiUsers,
  FiArchive,
  FiRefreshCw,
} = FiIcons;

const PlayerSponsorAdminPanel = () => {
  const [players, setPlayers] = useState([]);
  const [activityView, setActivityView] = useState('active');
  const [selectedId, setSelectedId] = useState('');
  const [formData, setFormData] = useState({
    sponsor_name: '',
    sponsor_image_url: '',
    sponsor_url: '',
  });
  const [imageFile, setImageFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [statusSaving, setStatusSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const { uploadFile, uploading, error: uploadError } = useStorage();

  const activePlayers = useMemo(
    () => players.filter((player) => player.is_active !== false),
    [players]
  );

  const inactivePlayers = useMemo(
    () => players.filter((player) => player.is_active === false),
    [players]
  );

  const visiblePlayers = activityView === 'inactive' ? inactivePlayers : activePlayers;

  const selectedPlayer = useMemo(
    () => players.find((player) => player.id === selectedId) || null,
    [players, selectedId]
  );

  const fetchPlayers = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('team_members_aox1234')
        .select(
          'id,name,position,number,image_url,sponsor_name,sponsor_image_url,sponsor_url,sort_order,is_active,is_published,sportiko_player_id'
        )
        .eq('is_staff', false)
        .order('sort_order', { ascending: true })
        .order('name', { ascending: true });

      if (error) throw error;
      setPlayers(data || []);
    } catch (err) {
      setMessage({ type: 'error', text: `Αποτυχία φόρτωσης παικτριών: ${err.message}` });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlayers();
  }, []);

  useEffect(() => {
    if (selectedId && !visiblePlayers.some((player) => player.id === selectedId)) {
      setSelectedId('');
    }
  }, [activityView, selectedId, visiblePlayers]);

  useEffect(() => {
    if (!selectedPlayer) {
      setFormData({ sponsor_name: '', sponsor_image_url: '', sponsor_url: '' });
      setImageFile(null);
      setPreviewUrl('');
      return;
    }

    setFormData({
      sponsor_name: selectedPlayer.sponsor_name || '',
      sponsor_image_url: selectedPlayer.sponsor_image_url || '',
      sponsor_url: selectedPlayer.sponsor_url || '',
    });
    setImageFile(null);
    setPreviewUrl('');
    setMessage({ type: '', text: '' });
  }, [selectedPlayer]);

  const handleImageChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      setMessage({ type: 'error', text: 'Χρησιμοποίησε JPG, PNG, WebP ή SVG.' });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'Το λογότυπο πρέπει να είναι μικρότερο από 5MB.' });
      return;
    }

    setImageFile(file);
    const reader = new FileReader();
    reader.onload = (e) => setPreviewUrl(e.target.result);
    reader.readAsDataURL(file);
  };

  const handleSave = async (event) => {
    event.preventDefault();
    if (!selectedPlayer) return;

    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      let sponsorImageUrl = formData.sponsor_image_url;

      if (imageFile) {
        const uploadedUrl = await uploadFile(imageFile, 'team_logos', 'player-sponsors/');
        if (!uploadedUrl) throw new Error(uploadError || 'Αποτυχία μεταφόρτωσης λογότυπου');
        sponsorImageUrl = uploadedUrl;
      }

      const { error } = await supabase
        .from('team_members_aox1234')
        .update({
          sponsor_name: formData.sponsor_name.trim() || null,
          sponsor_image_url: sponsorImageUrl || null,
          sponsor_url: formData.sponsor_url.trim() || null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', selectedPlayer.id);

      if (error) throw error;

      setMessage({ type: 'success', text: `Ο χορηγός της ${selectedPlayer.name} αποθηκεύτηκε.` });
      await fetchPlayers();
    } catch (err) {
      setMessage({ type: 'error', text: `Σφάλμα: ${err.message}` });
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    if (!selectedPlayer) return;
    if (!window.confirm(`Να αφαιρεθεί ο χορηγός από τη ${selectedPlayer.name};`)) return;

    setSaving(true);
    try {
      const { error } = await supabase
        .from('team_members_aox1234')
        .update({
          sponsor_name: null,
          sponsor_image_url: null,
          sponsor_url: null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', selectedPlayer.id);

      if (error) throw error;
      setMessage({ type: 'success', text: 'Η χορηγία αφαιρέθηκε από την παίκτρια.' });
      await fetchPlayers();
    } catch (err) {
      setMessage({ type: 'error', text: `Σφάλμα: ${err.message}` });
    } finally {
      setSaving(false);
    }
  };

  const handleManualActivityChange = async (nextActive) => {
    if (!selectedPlayer || selectedPlayer.sportiko_player_id) return;

    const action = nextActive ? 'ενεργή' : 'ανενεργή / στο αρχείο';
    if (!window.confirm(`Να οριστεί η ${selectedPlayer.name} ως ${action};`)) return;

    setStatusSaving(true);
    setMessage({ type: '', text: '' });

    try {
      const { error } = await supabase
        .from('team_members_aox1234')
        .update({ is_active: nextActive, updated_at: new Date().toISOString() })
        .eq('id', selectedPlayer.id)
        .is('sportiko_player_id', null);

      if (error) throw error;

      setMessage({
        type: 'success',
        text: nextActive
          ? `${selectedPlayer.name}: ενεργή παίκτρια.`
          : `${selectedPlayer.name}: μεταφέρθηκε στις ανενεργές / αρχείο. Δεν εμφανίζεται πλέον στο τρέχον ρόστερ ούτε δέχεται νέα χορηγία.`,
      });
      setSelectedId('');
      setActivityView(nextActive ? 'active' : 'inactive');
      await fetchPlayers();
    } catch (err) {
      setMessage({ type: 'error', text: `Αποτυχία αλλαγής κατάστασης: ${err.message}` });
    } finally {
      setStatusSaving(false);
    }
  };

  const sponsoredCount = players.filter(
    (player) => player.sponsor_name || player.sponsor_image_url
  ).length;

  if (loading) {
    return <div className="py-16 text-center text-gray-500">Φόρτωση παικτριών...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-primary-100 bg-gradient-to-r from-primary-50 to-white p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-xl font-black text-gray-900">Χορηγοί Παικτριών</h2>
            <p className="mt-1 max-w-3xl text-sm text-gray-600">
              Οι ενεργές παίκτριες είναι το σημερινό ρόστερ και μπορούν να δεχθούν νέα χορηγία. Οι ανενεργές παραμένουν στο αρχείο μαζί με την ιστορική χορηγία τους — δεν διαγράφονται.
            </p>
          </div>
          <div className="rounded-full bg-primary-600 px-4 py-2 text-sm font-black text-white">
            {sponsoredCount}/{players.length} με καταχωρημένο χορηγό
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 rounded-2xl border border-gray-100 bg-white p-2 shadow-sm">
        <button
          type="button"
          onClick={() => {
            setActivityView('active');
            setSelectedId('');
          }}
          className={`flex items-center justify-center rounded-xl px-4 py-3 text-sm font-black transition ${
            activityView === 'active'
              ? 'bg-green-600 text-white shadow-md'
              : 'text-gray-600 hover:bg-green-50 hover:text-green-700'
          }`}
        >
          <SafeIcon icon={FiUsers} className="mr-2 h-4 w-4" />
          Ενεργές ({activePlayers.length})
        </button>
        <button
          type="button"
          onClick={() => {
            setActivityView('inactive');
            setSelectedId('');
          }}
          className={`flex items-center justify-center rounded-xl px-4 py-3 text-sm font-black transition ${
            activityView === 'inactive'
              ? 'bg-gray-800 text-white shadow-md'
              : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
          }`}
        >
          <SafeIcon icon={FiArchive} className="mr-2 h-4 w-4" />
          Ανενεργές / Αρχείο ({inactivePlayers.length})
        </button>
      </div>

      {message.text && (
        <div
          className={`flex items-center gap-2 rounded-xl border p-4 text-sm font-semibold ${
            message.type === 'success'
              ? 'border-green-100 bg-green-50 text-green-700'
              : 'border-red-100 bg-red-50 text-red-700'
          }`}
        >
          <SafeIcon icon={message.type === 'success' ? FiCheckCircle : FiAlertCircle} className="h-5 w-5" />
          {message.text}
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[320px_1fr]">
        <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
          <label className="mb-2 block text-sm font-black text-gray-800">
            {activityView === 'active' ? 'Επίλεξε ενεργή παίκτρια' : 'Επίλεξε παίκτρια από το αρχείο'}
          </label>
          <select
            value={selectedId}
            onChange={(event) => setSelectedId(event.target.value)}
            className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-3 text-sm outline-none focus:border-primary-400 focus:ring-4 focus:ring-primary-100"
          >
            <option value="">— Παίκτρια —</option>
            {visiblePlayers.map((player) => (
              <option key={player.id} value={player.id}>
                {player.number ? `#${player.number} · ` : ''}{player.name}{player.sponsor_name ? ' ✓' : ''}
              </option>
            ))}
          </select>

          {visiblePlayers.length === 0 && (
            <p className="mt-4 rounded-xl bg-gray-50 p-3 text-xs text-gray-500">
              Δεν υπάρχουν παίκτριες σε αυτή την κατηγορία.
            </p>
          )}

          <div className="mt-5 space-y-2 border-t border-gray-100 pt-4 text-xs text-gray-500">
            <p><strong>✓</strong> σημαίνει ότι υπάρχει καταχωρημένη ιστορική ή τρέχουσα χορηγία.</p>
            <p>Οι ανενεργές δεν εμφανίζονται στο δημόσιο τρέχον ρόστερ και δεν έχουν κουμπί «Γίνε Χορηγός».</p>
          </div>
        </div>

        {!selectedPlayer ? (
          <div className="flex min-h-[320px] items-center justify-center rounded-2xl border border-dashed border-gray-200 bg-white p-8 text-center text-gray-500">
            <div>
              <SafeIcon icon={activityView === 'active' ? FiUser : FiArchive} className="mx-auto mb-3 h-10 w-10 text-gray-300" />
              {activityView === 'active'
                ? 'Επίλεξε ενεργή παίκτρια για να ορίσεις τον προσωπικό της χορηγό.'
                : 'Επίλεξε παίκτρια από το αρχείο για να δεις ή να διατηρήσεις την ιστορική χορηγία της.'}
            </div>
          </div>
        ) : (
          <motion.form
            key={selectedPlayer.id}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            onSubmit={handleSave}
            className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm sm:p-6"
          >
            <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="h-16 w-16 overflow-hidden rounded-full bg-gray-100">
                  <ImageWithFallback src={selectedPlayer.image_url} alt={selectedPlayer.name} className="h-full w-full object-cover object-top" />
                </div>
                <div>
                  <div className="mb-1 flex flex-wrap items-center gap-2">
                    <h3 className="text-lg font-black text-gray-900">{selectedPlayer.name}</h3>
                    <span
                      className={`rounded-full px-2.5 py-1 text-[11px] font-black ${
                        selectedPlayer.is_active !== false
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-200 text-gray-700'
                      }`}
                    >
                      {selectedPlayer.is_active !== false ? 'ΕΝΕΡΓΗ' : 'ΑΝΕΝΕΡΓΗ · ΑΡΧΕΙΟ'}
                    </span>
                  </div>
                  <p className="text-sm font-semibold text-primary-700">{selectedPlayer.position}</p>
                  {selectedPlayer.sportiko_player_id && (
                    <p className="mt-1 flex items-center text-xs font-semibold text-gray-500">
                      <SafeIcon icon={FiRefreshCw} className="mr-1.5 h-3.5 w-3.5" />
                      Η κατάσταση συγχρονίζεται από fin.sportiko.eu
                    </p>
                  )}
                </div>
              </div>

              {!selectedPlayer.sportiko_player_id && (
                <button
                  type="button"
                  disabled={statusSaving}
                  onClick={() => handleManualActivityChange(selectedPlayer.is_active === false)}
                  className={`rounded-xl px-4 py-2.5 text-sm font-black transition disabled:opacity-60 ${
                    selectedPlayer.is_active !== false
                      ? 'border border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                      : 'bg-green-600 text-white hover:bg-green-700'
                  }`}
                >
                  {statusSaving
                    ? 'Αποθήκευση...'
                    : selectedPlayer.is_active !== false
                      ? 'Μεταφορά στο Αρχείο'
                      : 'Επαναφορά ως Ενεργή'}
                </button>
              )}
            </div>

            {selectedPlayer.is_active === false && (
              <div className="mb-6 rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
                <strong className="text-gray-900">Ιστορικό αρχείο:</strong> η παίκτρια παραμένει στη βάση μαζί με το λογότυπο και το link του χορηγού που είχε. Δεν προβάλλεται ως διαθέσιμη για νέα χορηγία.
              </div>
            )}

            <div className="grid grid-cols-1 gap-6 md:grid-cols-[220px_1fr]">
              <div>
                <label className="mb-2 block text-sm font-black text-gray-800">Λογότυπο χορηγού</label>
                <div className="flex aspect-[3/2] items-center justify-center overflow-hidden rounded-xl border border-gray-200 bg-gray-50 p-4">
                  <ImageWithFallback
                    src={previewUrl || formData.sponsor_image_url}
                    alt={formData.sponsor_name || 'Χορηγός'}
                    className="max-h-full max-w-full object-contain"
                  />
                </div>
                <label className="mt-3 flex cursor-pointer items-center justify-center rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm font-bold text-gray-700 transition hover:bg-gray-50">
                  <SafeIcon icon={FiUpload} className="mr-2 h-4 w-4" />
                  Επιλογή εικόνας
                  <input type="file" accept="image/*" onChange={handleImageChange} className="sr-only" />
                </label>
              </div>

              <div className="space-y-4">
                <label className="block">
                  <span className="mb-1.5 block text-sm font-black text-gray-800">Όνομα χορηγού</span>
                  <input
                    type="text"
                    value={formData.sponsor_name}
                    onChange={(event) => setFormData((prev) => ({ ...prev, sponsor_name: event.target.value }))}
                    placeholder="π.χ. Example Company"
                    className="w-full rounded-xl border border-gray-200 px-4 py-3 outline-none focus:border-primary-400 focus:ring-4 focus:ring-primary-100"
                  />
                </label>

                <label className="block">
                  <span className="mb-1.5 block text-sm font-black text-gray-800">Link χορηγού <span className="font-normal text-gray-400">(προαιρετικό)</span></span>
                  <div className="relative">
                    <SafeIcon icon={FiLink} className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                    <input
                      type="url"
                      value={formData.sponsor_url}
                      onChange={(event) => setFormData((prev) => ({ ...prev, sponsor_url: event.target.value }))}
                      placeholder="https://..."
                      className="w-full rounded-xl border border-gray-200 py-3 pl-11 pr-4 outline-none focus:border-primary-400 focus:ring-4 focus:ring-primary-100"
                    />
                  </div>
                  <p className="mt-1.5 text-xs text-gray-500">Αν υπάρχει link, όποιος πατά το λογότυπο μεταφέρεται εκεί.</p>
                </label>

                <div className="flex flex-wrap gap-3 pt-2">
                  <button
                    type="submit"
                    disabled={saving || uploading}
                    className="inline-flex items-center rounded-xl bg-primary-600 px-5 py-3 font-black text-white transition hover:bg-primary-700 disabled:opacity-60"
                  >
                    <SafeIcon icon={FiSave} className="mr-2 h-4 w-4" />
                    {saving || uploading ? 'Αποθήκευση...' : 'Αποθήκευση Χορηγού'}
                  </button>

                  {(selectedPlayer.sponsor_name || selectedPlayer.sponsor_image_url || selectedPlayer.sponsor_url) && (
                    <button
                      type="button"
                      onClick={handleRemove}
                      disabled={saving}
                      className="inline-flex items-center rounded-xl border border-red-200 bg-white px-5 py-3 font-bold text-red-600 transition hover:bg-red-50 disabled:opacity-60"
                    >
                      <SafeIcon icon={FiTrash2} className="mr-2 h-4 w-4" />
                      Αφαίρεση
                    </button>
                  )}
                </div>
              </div>
            </div>
          </motion.form>
        )}
      </div>
    </div>
  );
};

export default PlayerSponsorAdminPanel;